import { useNavigate } from "react-router-dom"
import { useSelector } from "react-redux"
import { selectCurrentOrganizationId } from "@/modules/organizations/store/organizationSlice"
import {
  DataTable,
  TableColumn,
} from "@/modules/shared/ui/components/custom/CustomTable"
import { useEmailTemplates } from "../../hooks/useEmailTemplates"
import type { EmailTemplateDto } from "../../dtos/email-templates-dto"

type EmailTemplateGroup =
  | "Recruitment"
  | "Onboarding"
  | "Workflows"
  | "Offboarding"
  | "Other"

const GROUP_ORDER: EmailTemplateGroup[] = [
  "Recruitment",
  "Onboarding",
  "Workflows",
  "Offboarding",
  "Other",
]

const TYPE_TO_GROUP: Record<string, EmailTemplateGroup> = {
  CandidateOffer: "Recruitment",
  CandidateRejection: "Recruitment",
  InterviewInvite: "Recruitment",
  VideoInterviewInvite: "Recruitment",
  ApplicationCommentMention: "Recruitment",
  CandidateAcceptedRecruitment: "Recruitment",
  InterviewTimeSelection: "Recruitment",
  VacancySuggestion: "Recruitment",
  VacancySuggestionTeamNotice: "Recruitment",
  VacancySuggestionResult: "Recruitment",

  EmployeeInvite: "Onboarding",
  OrganizationInvite: "Onboarding",
  EmployeeOnboardingApproved: "Onboarding",
  EmployeeOnboardingFlagged: "Onboarding",
  EmployeeOnboardingSubmitted: "Onboarding",
  EmployeeOnboardingFlagsResolved: "Onboarding",
  CandidateAcceptedOnboarding: "Onboarding",

  WorkflowTaskReactivated: "Workflows",
  WorkflowTaskCancelled: "Workflows",
  WorkflowTaskAdminNotification: "Workflows",
  WorkflowTaskAssignment: "Workflows",

  ResignationApproved: "Offboarding",
  ResignationDisapproved: "Offboarding",
  OffboardingStartedEmployee: "Offboarding",
  OffboardingStartedAdmin: "Offboarding",
}

function humanizeType(type: string): string {
  return type.replace(/([A-Z])/g, " $1").trim()
}

function getGroup(type: string): EmailTemplateGroup {
  return TYPE_TO_GROUP[type] ?? "Other"
}

type EmailTemplateRow = EmailTemplateDto & { id: string }

export default function EmailTemplatesListPage() {
  const navigate = useNavigate()
  const organizationId = useSelector(selectCurrentOrganizationId)
  const { data: templates = [], isLoading } = useEmailTemplates(
    organizationId ?? undefined,
  )

  // Backend flags system-only templates (OtpCode, ResetPassword, SubscriptionActivated,
  // PaymentFailed) — keep that list server-side so it can't drift between layers.
  const rows: EmailTemplateRow[] = templates
    .filter((t) => !t.isSystemOnly)
    .map((t) => ({ ...t, id: t.type }))

  const rowsByGroup = new Map<EmailTemplateGroup, EmailTemplateRow[]>()
  for (const row of rows) {
    const group = getGroup(row.type)
    const bucket = rowsByGroup.get(group) ?? []
    bucket.push(row)
    rowsByGroup.set(group, bucket)
  }

  const columns: TableColumn<EmailTemplateRow>[] = [
    {
      key: "type",
      label: "Template",
      render: (t) => (
        <span className="font-medium text-foreground">
          {humanizeType(t.type)}
        </span>
      ),
    },
    {
      key: "subject",
      label: "Subject",
      render: (t) => {
        const parts = t.subject.split(/({{[^}]+}})/)
        return (
          <span className="text-muted-foreground">
            {parts.map((part, i) =>
              part.startsWith("{{") && part.endsWith("}}") ? (
                <span
                  key={i}
                  className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-primary/10 text-primary mx-0.5"
                >
                  {t.subjectSampleData?.[part] ?? t.sampleData?.[part] ?? part}
                </span>
              ) : (
                <span key={i}>{part}</span>
              )
            )}
          </span>
        )
      },
    },
    {
      key: "customBody",
      label: "Status",
      render: (t) =>
        t.customBody ? (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
            Customised
          </span>
        ) : (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-muted text-muted-foreground">
            Default
          </span>
        ),
    },
  ]

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground tracking-tight">
          Email Templates
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Customise the emails your organisation sends to candidates and
          employees.
        </p>
      </div>

      {isLoading ? (
        <div className="bg-card rounded-xl shadow-sm border border-border overflow-hidden">
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          </div>
        </div>
      ) : (
        GROUP_ORDER.filter((g) => (rowsByGroup.get(g) ?? []).length > 0).map(
          (group) => {
            const groupRows = rowsByGroup.get(group) ?? []
            return (
              <section key={group} className="space-y-3">
                <h2 className="text-lg font-semibold text-foreground">
                  {group}
                </h2>
                <div className="bg-card rounded-xl shadow-sm border border-border overflow-hidden">
                  <DataTable<EmailTemplateRow>
                    columns={columns}
                    data={groupRows}
                    emptyMessage="No email templates in this group."
                    onRowClick={(t) =>
                      navigate(`/settings/email-templates/${t.type}`)
                    }
                    actions={(t) => [
                      {
                        label: "Edit",
                        onClick: () =>
                          navigate(`/settings/email-templates/${t.type}`),
                      },
                    ]}
                  />
                </div>
              </section>
            )
          },
        )
      )}
    </div>
  )
}
